import { Book, Clock, Flame, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'

import { ReadHistory } from '@/api/type'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMinutesJa } from '@/lib/time-format'

type Props = {
  history: ReadHistory[]
  isLoading: boolean
  selectedDate: Date
}

// ドメイン別のカラー
const DOMAIN_COLORS = new Map<string, string>([
  ['oreilly', 'bg-orange-500'],
  ['tver', 'bg-blue-500'],
  ['fod', 'bg-red-500'],
  ['abema', 'bg-emerald-500'],
  ['line', 'bg-green-500'],
  ['archive', 'bg-purple-500'],
  ['local', 'bg-gray-500'],
])

// 時間帯のラベル
const TIME_LABELS = ['0-3時', '3-6時', '6-9時', '9-12時', '12-15時', '15-18時', '18-21時', '21-24時']

export const StatsView = ({ history, isLoading, selectedDate: _selectedDate }: Props) => {
  const stats = useMemo(() => {
    if (history.length === 0) {
      return {
        totalSessions: 0,
        totalMinutes: 0,
        avgSessionMinutes: 0,
        streakDays: 0,
        domainStats: [] as { domain: string; count: number; percentage: number }[],
        timeDistribution: [] as number[],
        weekdayStats: [] as number[],
      }
    }

    // 総セッション数
    const totalSessions = history.length

    // 総時間（分）
    const totalMinutes = history.reduce((sum, item) => {
      const start = new Date(item.started_at).getTime()
      const end = new Date(item.ended_at).getTime()
      return sum + (end - start) / 60000
    }, 0)

    // 平均セッション時間
    const avgSessionMinutes = totalMinutes / totalSessions

    // 連続読書日数
    const dates = [...new Set(history.map((item) => new Date(item.started_at).toDateString()))].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    )

    let streakDays = 0
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let i = 0; i < dates.length; i++) {
      const expectedDate = new Date(today)
      expectedDate.setDate(expectedDate.getDate() - i)
      expectedDate.setHours(0, 0, 0, 0)

      const dateStr = dates.at(i)
      if (!dateStr) break
      const currentDate = new Date(dateStr)
      currentDate.setHours(0, 0, 0, 0)

      if (currentDate.getTime() === expectedDate.getTime()) {
        streakDays++
      } else if (i === 0 && currentDate.getTime() < expectedDate.getTime()) {
        // 今日読んでいなくても、昨日まで連続していればカウント
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        if (currentDate.getTime() === yesterday.getTime()) {
          streakDays++
        } else {
          break
        }
      } else {
        break
      }
    }

    // ドメイン別統計
    const domainCounts = new Map<string, number>()
    history.forEach((item) => {
      const current = domainCounts.get(item.product_domain) ?? 0
      domainCounts.set(item.product_domain, current + 1)
    })
    const domainStats = [...domainCounts.entries()]
      .map(([domain, count]) => ({
        domain,
        count,
        percentage: (count / totalSessions) * 100,
      }))
      .sort((a, b) => b.count - a.count)

    // 時間帯分布（3時間ごと）
    const timeDistribution = new Map<number, number>()
    history.forEach((item) => {
      const hour = new Date(item.started_at).getHours()
      const bucket = Math.floor(hour / 3)
      timeDistribution.set(bucket, (timeDistribution.get(bucket) ?? 0) + 1)
    })
    const timeDistributionArray = Array.from({ length: 8 }, (_, i) => timeDistribution.get(i) ?? 0)

    // 曜日別統計
    const weekdayMap = new Map<number, number>()
    history.forEach((item) => {
      const day = new Date(item.started_at).getDay()
      weekdayMap.set(day, (weekdayMap.get(day) ?? 0) + 1)
    })
    const weekdayStats = Array.from({ length: 7 }, (_, i) => weekdayMap.get(i) ?? 0)

    return {
      totalSessions,
      totalMinutes: Math.round(totalMinutes),
      avgSessionMinutes: Math.round(avgSessionMinutes),
      streakDays,
      domainStats,
      timeDistribution: timeDistributionArray,
      weekdayStats,
    }
  }, [history])

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-48" />
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4 p-8">
        <TrendingUp className="size-16 opacity-50" />
        <p>統計を表示するには読書履歴が必要です</p>
      </div>
    )
  }

  const maxTimeValue = Math.max(...stats.timeDistribution)
  const maxWeekdayValue = Math.max(...stats.weekdayStats)

  return (
    <div className="p-4 space-y-4">
      {/* サマリーカード */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Book className="size-5 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.totalSessions}</div>
              <div className="text-xs text-muted-foreground">総セッション</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Clock className="size-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">{formatMinutesJa(stats.totalMinutes)}</div>
              <div className="text-xs text-muted-foreground">総読書時間</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="size-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">{formatMinutesJa(stats.avgSessionMinutes)}</div>
              <div className="text-xs text-muted-foreground">平均セッション</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Flame className="size-5 text-orange-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.streakDays}日</div>
              <div className="text-xs text-muted-foreground">連続読書</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ドメイン別 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">ドメイン別</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {stats.domainStats.map(({ domain, count, percentage }) => {
            const colorClass = DOMAIN_COLORS.get(domain) ?? 'bg-muted'
            return (
              <div key={domain} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${colorClass}`} />
                <span className="text-sm flex-1">{domain}</span>
                <span className="text-sm text-muted-foreground">{count}回</span>
                <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${colorClass}`} style={{ width: `${percentage}%` }} />
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* 時間帯分布 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">よく読む時間帯</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-24">
            {stats.timeDistribution.map((count, i) => {
              const label = TIME_LABELS.at(i) ?? ''
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-primary rounded-t"
                    style={{
                      height: `${maxTimeValue > 0 ? (count / maxTimeValue) * 100 : 0}%`,
                      minHeight: count > 0 ? '4px' : '0',
                    }}
                  />
                  <span className="text-[10px] text-muted-foreground">{label}</span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* 曜日別 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">曜日別</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-20">
            {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => {
              const value = stats.weekdayStats.at(i) ?? 0
              return (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-t ${i === 0 ? 'bg-red-500' : i === 6 ? 'bg-blue-500' : 'bg-primary'}`}
                    style={{
                      height: `${maxWeekdayValue > 0 ? (value / maxWeekdayValue) * 100 : 0}%`,
                      minHeight: value > 0 ? '4px' : '0',
                    }}
                  />
                  <span
                    className={`text-xs ${i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : 'text-muted-foreground'}`}
                  >
                    {day}
                  </span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
